/* eslint-disable react-hooks/exhaustive-deps */
import DirectorNotesButton from "components/DirectorNotesButton";
import DocumentViewerModal from "components/DocumentViewer";
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import InvestigationInformationTab from "pages/InvestigationFileDetail/fragment/InvestigationInformationTab";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import {
  editCrimeReferences2,
  fetchCrimeReferencesByCRNumberAndIncidentRegNo,
  fetchCrimeReferencesById,
} from "services/incidentService";
import {
  CrimeReference,
  CrimeType,
  FEEDBACK_MODEL,
  FILE_URL,
  Investigation,
  InvestigationFile,
  Roles,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm, hasRole } from "utils/helper";
import AssignedToTab from "./AssignedToTab";
import EvidenceFilesTab from "./EvidenceFilesTab";
import ExhibitsTab from "./ExhibitsTab";
import CrimeFinalInformation from "./fragment/CrimeFinalInformation";
import Forward from "./fragment/Forward";
import { useCourtFilingMarks } from "components/CourtFilingMarks";
import ScreeningMembers from "./fragment/ScreeningMembers";
import IncidentInformationTab from "./IncidentInformationTab";
import "./incidentRegister.css";
import InvestigationTab from "./InvestigationTab";
import EconomicInvestigationForm from "./modals/EconomicInvestigationForm";
import ReporterTab from "./ReporterTab";
import SuspectTab from "./SuspectTab";
import VictimsTab from "./VictimsTab";
import WitnessesTab from "./WitnessesTab";

const TAB_INCIDENT_INFORMATION = "incident_information";
const TAB_REPORTER = "reporter";
const TAB_EVIDENCE_FILES = "evidence_files";
const TAB_VICTIMS = "victims";
const TAB_SUSPECTS = "suspects";
const TAB_WITNESS = "witness";
const TAB_EXHIBITS = "exhibits";
const TAB_INVESTIGATION = "investigation";
const TAB_SCREENING = "screening";
const TAB_ASSIGNED_TO = "assigned_to";
const TAB_INVESTIGATION_FILE = "investigation_file";

const tab1 = [
  TAB_INCIDENT_INFORMATION,
  TAB_REPORTER,
  TAB_EVIDENCE_FILES,
  TAB_VICTIMS,
  TAB_SUSPECTS,
  TAB_WITNESS,
  TAB_EXHIBITS,
];
const tab2 = [...tab1, TAB_INVESTIGATION];
const tab3 = [...tab1, TAB_SCREENING];
const tab4 = [...tab1, TAB_SCREENING, TAB_INVESTIGATION];

type Props = {
  crimeReferenceNumber?: string;
  incidentRegistrationNumber?: string;
  file?: InvestigationFile;
};

const IncidentDetail: React.FC<Props> = ({
  crimeReferenceNumber,
  incidentRegistrationNumber,
  file,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { state: locationState } = useLocation();

  const [tabs, setTabs] = useState(tab1);
  const [activeTab, setActiveTab] = useState(tab1[0]);
  const params = useParams();
  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [refetch, setRefetch] = useState(true);

  const [loading, setLoading] = useState(true);

  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [incident, setIncident] = useState<CrimeReference>();

  const [changeTab, setChangeTab] = useState("");
  const [isOpenInvestigationForm, setIsOpenInvestigationForm] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState("");

  const normalizeTab = (tab?: string): string | undefined => {
    switch (tab) {
      case TAB_INCIDENT_INFORMATION:
      case "Incident Information":
        return TAB_INCIDENT_INFORMATION;
      case TAB_REPORTER:
      case "Reporter":
        return TAB_REPORTER;
      case TAB_EVIDENCE_FILES:
      case "Evidence Files":
        return TAB_EVIDENCE_FILES;
      case TAB_VICTIMS:
      case "Victims":
        return TAB_VICTIMS;
      case TAB_SUSPECTS:
      case "Suspects":
        return TAB_SUSPECTS;
      case TAB_WITNESS:
      case "Witness":
        return TAB_WITNESS;
      case TAB_EXHIBITS:
      case "Exhibits":
        return TAB_EXHIBITS;
      case TAB_INVESTIGATION:
      case "Investigation":
        return TAB_INVESTIGATION;
      case TAB_SCREENING:
      case "Screening":
        return TAB_SCREENING;
      case TAB_ASSIGNED_TO:
      case "Assigned to":
        return TAB_ASSIGNED_TO;
      case TAB_INVESTIGATION_FILE:
      case "Investigation File":
        return TAB_INVESTIGATION_FILE;
      default:
        return undefined;
    }
  };

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case TAB_INCIDENT_INFORMATION:
        return t("incident_information");
      case TAB_REPORTER:
        return t("reporter");
      case TAB_EVIDENCE_FILES:
        return t("evidence_files");
      case TAB_VICTIMS:
        return t("victims");
      case TAB_SUSPECTS:
        return t("suspects");
      case TAB_WITNESS:
        return t("witness");
      case TAB_EXHIBITS:
        return t("exhibits");
      case TAB_INVESTIGATION:
        return t("investigation");
      case TAB_SCREENING:
        return t("screening");
      case TAB_ASSIGNED_TO:
        return t("assigned_to");
      case TAB_INVESTIGATION_FILE:
        return t("investigation_file");
      default:
        return tab;
    }
  };

  const resolveActiveTab = (selectedTab: string[]) => {
    if (file) {
      return TAB_INVESTIGATION_FILE;
    }

    const nextTab = normalizeTab(changeTab) || normalizeTab(locationState?.tab);
    if (nextTab && selectedTab.includes(nextTab)) {
      return nextTab;
    }

    // Registrar lands directly on the Screening tab to register / send to
    // police, and to review the details once it has been sent.
    if (
      isRegistrar &&
      (incident?.status === "forwarded to registrar" ||
        incident?.status === "sent_to_police") &&
      selectedTab.includes(TAB_SCREENING)
    ) {
      return TAB_SCREENING;
    }

    return selectedTab[0];
  };

  const canManageIncidentDetail = hasRole(loginData?.roles, [
    Roles.prosecutor,
    Roles.member,
    Roles.clerk,
    Roles.economicClerk,
    Roles.corruptionClerk,
    Roles.economicDirector,
    Roles.screeningTeamCoordinator,
    Roles.corruptionDirector,
  ]);

  useEffect(() => {
    if (params?.incidentId) {
      setLoading(true);
      fetchCrimeReferencesById(params?.incidentId)
        .then((data) => {
          setIncident(data);
          setInvestigations(data?.investigations || []);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          showTooltip(
            error?.response?.data?.detail || errorHandler(error),
            "error",
          );
        });
    }
  }, [params?.incidentId, refetch]);

  useEffect(() => {
    if (crimeReferenceNumber && incidentRegistrationNumber) {
      setLoading(true);
      fetchCrimeReferencesByCRNumberAndIncidentRegNo(
        crimeReferenceNumber ?? "",
        incidentRegistrationNumber ?? "",
      )
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            setIncident(data[0]);
            setInvestigations(data[0]?.investigations || []);
          }
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          showTooltip(
            error?.response?.data?.detail || errorHandler(error),
            "error",
          );
        });
    }
  }, [crimeReferenceNumber, incidentRegistrationNumber, refetch]);

  // useEffect(() => {
  //   if (refetch) {
  // setLoading(true);
  // fetchInvestigation()
  //   .then((response) => {
  //     const filtered = response.filter(
  //       (investigation) =>
  //         investigation.crime_reference?.uu_id?.toString() ===
  //         params?.incidentId?.toString()
  //     );
  //     setInvestigations(filtered);
  //     setLoading(false);
  //   })
  //   .catch((error) => {
  //     setLoading(false);
  //     showTooltip(
  //       error?.response?.data?.detail || "Error fetching data",
  //       "error"
  //     );
  //   });
  // setRefetch(false);
  //   }
  // }, [params?.incidentId, refetch]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isScreeningTeamCoordinator = hasRole(loginData?.roles, [
    Roles.screeningTeamCoordinator,
  ]);
  const isDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
    Roles.corruptionDirector,
  ]);
  const isRegistrar = hasRole(loginData?.roles, [
    Roles.registrar,
    Roles.economicRegistrar, // "CaseCorruptionRegistrar" (names swapped in types)
    Roles.corruptionRegistrar, // "CaseEconomicRegistrar"
  ]);
  // Office stamp and signature on the crime reference sent to the police.
  const policeMarks = useCourtFilingMarks(
    isRegistrar && incident?.status === "forwarded to registrar",
  );

  // Renders the investigation's forwarded file (documents viewer, link fallback,
  // or an empty-state message). Shared by the director and registrar cards.
  const renderInvestigationFile = () => {
    const inv = incident?.investigations?.[0];
    if (inv?.documents && inv.documents.length > 0) {
      return (
        <DocumentViewerModal
          documents={inv.documents || []}
          baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.INVESTIGATION}`}
          layout="list"
          hideEmptyFile={true}
        />
      );
    }
    if (inv?.ec_reference_result_file_details?.url) {
      return (
        <a
          href={inv.ec_reference_result_file_details.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm text-red-600 hover:underline"
        >
          📄{" "}
          {inv.ec_reference_result_file_details.file_name ||
            t("investigation_file")}
        </a>
      );
    }
    return <p className="text-sm text-gray-500">{t("no_file_uploaded")}</p>;
  };

  useEffect(() => {
    if (incident && incident?.crime_type) {
      if (
        CrimeType[incident?.crime_type] === CrimeType.various_crime ||
        incident?.is_letter
      ) {
        // const selectedTab = investigations?.length > 0 ? tab2 : tab1;
        const selectedTab =
          incident?.decision_date || incident?.is_letter ? tab2 : tab2;
        setTabs(selectedTab);
        setActiveTab(resolveActiveTab(selectedTab));
      } else {
        // const selectedTab = investigations?.length > 0 ? tab4 : tab3;
        const selectedTab = isProsecutor
          ? tab1
          : isCoordinator &&
              incident?.status !== "forwarded to cordinatorforscreening" &&
              incident?.screening_members &&
              incident?.screening_members?.length > 0 &&
              incident?.screening_members?.find((s) => s?.team_leader) &&
              incident?.decision_type === "pending"
            ? tab1
            : CrimeType[incident?.crime_type] === CrimeType.various_crime
              ? incident?.decision_date
                ? tab4
                : tab3
              : incident?.screening_outcome === "approved"
                ? tab4
                : tab3;

        setTabs(selectedTab);
        setActiveTab(resolveActiveTab(selectedTab));
      }

      // setChangeTab("");
    }
  }, [incident, investigations, locationState, file]);

  return (
    <div>
      {(incident?.uu_id || (incident as any)?.id) && (
        <DirectorNotesButton
          model={FEEDBACK_MODEL.crimeReference}
          objectId={(incident?.uu_id || (incident as any)?.id) as any}
        />
      )}
      {loading ? (
        <Loading label={t("loading_incident")} />
      ) : (
        <div className="flex lg:flex-row md:flex-col gap-4">
          <div className="incidentRegistration mb-11">
            <div className="">
              {/* <TabNav active={activeTab} tabs={tabs} onSelect={setActiveTab} /> */}

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm  flex flex-col gap-2 transition-all duration-150 hover:shadow-lg">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">📋</span>
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">
                        {t("incident_details")}
                      </h1>
                      {/* <p className="text-gray-600 mt-1">
                        Manage incident information and related processes.
                      </p> */}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tabs.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => {
                          setActiveTab(tab);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors
                    ${
                      activeTab === tab
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-50"
                    }
                  `}
                      >
                        {getTabLabel(tab)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-6">
                  {activeTab === TAB_INVESTIGATION_FILE && file ? (
                    <InvestigationInformationTab
                      file={file}
                      setRefetch={setRefetch}
                    />
                  ) : activeTab === TAB_INCIDENT_INFORMATION ? (
                    <IncidentInformationTab
                      incident={incident}
                      setRefetch={setRefetch}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_REPORTER ? (
                    <ReporterTab
                      investigation={investigations[0]}
                      incident={incident}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_EVIDENCE_FILES && incident ? (
                    <EvidenceFilesTab
                      investigation={investigations[0]}
                      incident={incident}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_VICTIMS && incident ? (
                    <VictimsTab
                      investigation={investigations[0]}
                      incident={incident}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_SUSPECTS && incident ? (
                    <SuspectTab
                      investigation={investigations[0]}
                      incident={incident}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_WITNESS && incident ? (
                    <WitnessesTab
                      investigation={investigations[0]}
                      incident={incident}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_EXHIBITS ? (
                    <ExhibitsTab
                      investigation={investigations[0]}
                      incident={incident}
                      canManageIncidentDetail={canManageIncidentDetail}
                    />
                  ) : activeTab === TAB_ASSIGNED_TO ? (
                    <AssignedToTab />
                  ) : activeTab === TAB_INVESTIGATION ? (
                    <InvestigationTab
                      incident={incident}
                      investigations={investigations}
                      setRefetch={setRefetch}
                      setChangeTab={setChangeTab}
                    />
                  ) : activeTab === TAB_SCREENING ? (
                    hasRole(loginData?.roles, [
                      Roles.screeningTeamCoordinator,
                      Roles.coordinator,
                      Roles.prosecutionDirector,
                      Roles.corruptionDirector,
                      // Registrar can view the screening details (read-only).
                      Roles.registrar,
                      Roles.economicRegistrar,
                      Roles.corruptionRegistrar,
                    ]) &&
                    incident?.screening_members &&
                    incident?.screening_members?.length > 0 &&
                    incident?.screening_members?.find((s) => s?.team_leader) ? (
                      <div className="w-full lg:w-3/4">
                        <CrimeFinalInformation
                          incident={incident}
                          setRefetch={setRefetch}
                          setChangeTab={setChangeTab}
                        />
                      </div>
                    ) : null
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          {activeTab === TAB_SCREENING &&
            incident &&
            hasRole(loginData?.roles, [
              Roles.screeningTeamCoordinator,
              Roles.economicDirector,
              Roles.corruptionDirector,
            ]) &&
            // incident?.status === "forwarded to cordinatorforscreeningadditional" &&
            (incident?.screening_members?.length === 0 ||
              !incident?.screening_members?.find((s) => s?.team_leader)) && (
              <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                <ScreeningMembers
                  incident={incident}
                  setRefetch={setRefetch}
                  setChangeTab={setChangeTab}
                />
              </div>
            )}
          {/* Request investigation (right panel) after screening is approved */}
          {activeTab === TAB_SCREENING &&
            incident &&
            incident?.screening_outcome === "approved" &&
            isDirector &&
            incident?.investigations?.length === 0 && (
              <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 shrink-0 bg-emerald-50 rounded-xl flex items-center justify-center">
                      <span className="text-xl">🔍</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t("request_investigation")}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {t("request_investigation_hint")}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpenInvestigationForm(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                      />
                    </svg>
                    {t("request_investigation")}
                  </button>
                </div>
              </div>
            )}
          {/* After the investigation is requested, show where the file was sent
              (police station) and the forwarded file in the right panel. */}
          {activeTab === TAB_SCREENING &&
            incident &&
            incident?.screening_outcome === "approved" &&
            isDirector &&
            incident?.status !== "sent_to_police" &&
            incident?.investigations &&
            incident?.investigations?.length > 0 && (
              <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 shrink-0 bg-emerald-50 rounded-xl flex items-center justify-center">
                      <span className="text-xl">🗂️</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t("forward_to_registrar")}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {t("investigation_requested")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {t("police_station")}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {incident?.investigations?.[0]?.ec_police_station_name ||
                          "-"}
                      </p>
                    </div>

                    {incident?.investigations?.[0]?.sent_date_to_police && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {t("sent_date_to_police")}
                        </p>
                        <p className="text-sm text-gray-900">
                          {new Date(
                            incident?.investigations?.[0]?.sent_date_to_police,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        {t("investigation_file")}
                      </p>
                      {renderInvestigationFile()}
                    </div>
                  </div>

                  {incident?.status === "forwarded to registrar" ? (
                    <div className="mt-5 rounded-lg bg-amber-50 text-amber-800 px-4 py-3 text-sm">
                      {t("awaiting_registration")}
                    </div>
                  ) : (
                    <button
                      disabled={actionIsLoading}
                      onClick={() => {
                        setActionIsLoading(true);
                        editCrimeReferences2(incident.uu_id, {
                          status: "forwarded to registrar",
                        })
                          .then(() => {
                            showTooltip(t("successfully_submitted"), "success");
                            setRefetch((prev) => !prev);
                          })
                          .catch((error) => {
                            showTooltip(
                              error?.response?.data?.detail ||
                                errorHandlerOnForm(error),
                              "error",
                            );
                          })
                          .finally(() => setActionIsLoading(false));
                      }}
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("forward_to_registrar")}
                    </button>
                  )}
                </div>
              </div>
            )}
          {/* Registered & sent to police confirmation — visible to all roles. */}
          {activeTab === TAB_SCREENING &&
            incident &&
            incident?.status === "sent_to_police" && (
              <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 shrink-0 bg-emerald-50 rounded-xl flex items-center justify-center">
                      <span className="text-xl">✅</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t("sent_to_police")}
                      </h3>
                      <p className="text-sm text-emerald-700 mt-0.5">
                        {t("registered_and_sent_to_police")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {t("registration_number")}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {incident?.registration_number || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {t("police_station")}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {incident?.investigations?.[0]?.ec_police_station_name ||
                          "-"}
                      </p>
                    </div>
                    {incident?.investigations?.[0]?.sent_date_to_police && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {t("sent_date_to_police")}
                        </p>
                        <p className="text-sm text-gray-900">
                          {new Date(
                            incident?.investigations?.[0]?.sent_date_to_police,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        {t("investigation_file")}
                      </p>
                      {renderInvestigationFile()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          {/* Registrar: add a registration number and send to police */}
          {activeTab === TAB_SCREENING &&
            incident &&
            isRegistrar &&
            incident?.status === "forwarded to registrar" && (
              <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 shrink-0 bg-blue-50 rounded-xl flex items-center justify-center">
                      <span className="text-xl">🗂️</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t("registration_number")}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {t("awaiting_registration")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-5">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {t("police_station")}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {incident?.investigations?.[0]?.ec_police_station_name ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        {t("investigation_file")}
                      </p>
                      {renderInvestigationFile()}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("registration_number")}
                    </label>
                    <input
                      type="text"
                      placeholder="#123456"
                      value={registrationNumber}
                      onChange={(e) => setRegistrationNumber(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  {policeMarks.fields}

                  <button
                    disabled={actionIsLoading}
                    onClick={() => {
                      if (!registrationNumber) {
                        showTooltip(
                          t("please_enter_registration_number"),
                          "error",
                        );
                        return;
                      }
                      setActionIsLoading(true);
                      editCrimeReferences2(
                        incident.uu_id,
                        {
                          registration_number: registrationNumber,
                          status: "sent_to_police",
                        },
                        policeMarks.marks,
                      )
                        .then(() => {
                          showTooltip(t("successfully_submitted"), "success");
                          setRegistrationNumber("");
                          setRefetch((prev) => !prev);
                        })
                        .catch((error) => {
                          showTooltip(
                            error?.response?.data?.detail ||
                              errorHandlerOnForm(error),
                            "error",
                          );
                        })
                        .finally(() => setActionIsLoading(false));
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("send_to_police")}
                  </button>
                </div>
              </div>
            )}
          {isOpenInvestigationForm && incident && (
            <EconomicInvestigationForm
              action={"Add"}
              crime_reference_id={incident.uu_id}
              selectedFile={null}
              incident={incident}
              onClose={(refetch) => {
                setIsOpenInvestigationForm(false);
                if (refetch) {
                  setRefetch((prev) => !prev);
                }
              }}
            />
          )}
          {/* {isProsecutor &&
            incident?.status === "pending" &&
            incident?.crime_type !== "various_crime" && (
              <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                <button
                  className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors mt-5"
                  onClick={() => {
                    editCrimeReferences2(incident?.uu_id, {
                      status: "forwarded to cordinatorforscreeningadditional",
                    })
                      .then(() => {
                        showTooltip(t("successfully_revised"), "success");
                        setRefetch((prev) => !prev);
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                >
                  {actionIsLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      {LoadingIcon}
                      Loading...
                    </div>
                  ) : (
                    "Forward to Coordinator"
                  )}
                </button>
              </div>
            )} */}
          {isProsecutor &&
            activeTab === TAB_INVESTIGATION &&
            investigations.length > 0 &&
            incident && (
              <div>
                {investigations[0]?.end_date && (
                  <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-[calc(100vh-180px)] pr-8">
                    <Forward
                      incident={incident}
                      setRefetch={setRefetch}
                      setChangeTab={setChangeTab}
                      investigation={investigations[0]}
                    />
                  </div>
                )}
              </div>
            )}

          {(isCorruptionDirector ||
            isScreeningTeamCoordinator ||
            (isProsecutor &&
              incident?.screening_members &&
              incident?.screening_members?.length > 0)) &&
            incident?.crime_type !== "various_crime" &&
            incident?.status === "pending" &&
            incident?.decision_type === "pending" && (
              <div>
                <button
                  className="bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={actionIsLoading}
                  onClick={() => {
                    setActionIsLoading(true);
                    editCrimeReferences2(incident?.uu_id, {
                      status: "forwarded to cordinatorforscreening",
                    })
                      .then(() => {
                        showTooltip(t("successfully_submitted"), "success");
                        setRefetch((prev) => !prev);
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                >
                  {actionIsLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      {LoadingIcon}
                      {t("loading")}
                    </div>
                  ) : (
                    t("forward_to_coordinator_for_screening")
                  )}
                </button>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default IncidentDetail;
